import { useRef, useState } from 'react';
import { useFocusTrap } from '../useFocusTrap';
import { ShareRoom } from './ShareRoom';

export interface PauseOverlayProps {
  onResume: () => void;
  canResume: boolean;
  onOpenSettings: () => void;
  onOpenHelp: () => void;
  onLeave: () => void;
  code: string;
  /** PART 4: which seat triggered the (server-authoritative, room-wide)
   * pause -- `null` only defensively, for the instant before the first
   * `lobby` broadcast with a real value has arrived; GameScreen only mounts
   * this overlay while `status === 'paused'`, which in practice always has
   * a real `pausedBy` by then. */
  pausedBy: 1 | 2 | null;
  /** Which seat THIS client is -- decides the "You paused" vs "{name}
   * paused" branch below. */
  mySeat: 1 | 2;
  /** Display name of the OTHER seat, for the "{partnerName} paused" copy. */
  partnerName: string;
}

/** PART 4 fix: pause is room-wide (both clients get `status: 'paused'`),
 * but neither client used to be told WHO paused -- the partner could look
 * up mid-move with no explanation. Uses `pausedBy` (wire.ts) to make the
 * headline honest about which player triggered it, while keeping BOTH
 * players able to resume (the server allows either seat to resume
 * regardless of who paused -- `canResume` is unrelated to `pausedBy`,
 * still gated purely on connection state as before). */
function pauseHeadline(pausedBy: 1 | 2 | null, mySeat: 1 | 2, partnerName: string): string {
  if (pausedBy === null) return 'Paused';
  if (pausedBy === mySeat) return 'You paused the game for both players';
  return `${partnerName || 'Your partner'} paused the game`;
}

/** In-game pause menu: Resume, Share, Settings, How to Play, Leave --
 * still the same focus-trapped `role="dialog"` overlay as before (same
 * `.overlay`/`.overlay-card` scrim, same `pause-overlay` testid, same
 * Esc-to-resume mechanism -- that stays wired through GameScreen's
 * `attachKeyboard`, gated by `canResume`, completely unchanged; this
 * component does NOT re-handle Escape itself, see useFocusTrap's own
 * comment for why). Share swaps this card's body to the same `ShareRoom`
 * used in the Lobby (a "Back" returns to the menu) instead of opening a
 * second stacked overlay -- Settings/Help DO open as separate `Modal`s on
 * top (GameScreen owns that state), since those need their own
 * independent scroll/close lifecycle. */
export function PauseOverlay({
  onResume,
  canResume,
  onOpenSettings,
  onOpenHelp,
  onLeave,
  code,
  pausedBy,
  mySeat,
  partnerName,
}: PauseOverlayProps): JSX.Element {
  const [view, setView] = useState<'menu' | 'share'>('menu');
  const cardRef = useRef<HTMLDivElement>(null);

  useFocusTrap(cardRef, true);

  return (
    <div className="overlay pause-overlay" data-testid="pause-overlay">
      <div
        className="overlay-card pause-card"
        role="dialog"
        aria-modal="true"
        aria-label={view === 'share' ? 'Share this room' : 'Game paused'}
        ref={cardRef}
        tabIndex={-1}
      >
        {view === 'menu' ? (
          <>
            {/* Pixel-font usage policy: this headline is a real sentence
                ("You paused the game for both players" / "{name} paused the
                game") -- worst-readability item the critique named (#09) --
                moved to the body sans (`.title-sans`), same role/size step,
                just legible at sentence length. */}
            <h2 className="title-sans">{pauseHeadline(pausedBy, mySeat, partnerName)}</h2>
            <p className="dim">Press Esc or Resume to continue.</p>
            <div className="pause-menu-actions">
              <button type="button" className="btn btn-primary" onClick={onResume} disabled={!canResume} autoFocus>
                Resume
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setView('share')}>
                Share
              </button>
              {/* PER-SCREEN 09 fix: 4 equal-width outline buttons flattened the
                  whole menu into one undifferentiated block -- Resume (the
                  actual primary action) no longer read as more important than
                  Settings/How to Play. Resume stays the loud full-width
                  `btn-primary`; Share (a real, frequently-used action --
                  inviting a partner mid-game) stays full-width `btn-secondary`
                  too. Settings and How to Play are demoted into a smaller
                  2-column row (`.pause-menu-actions-row` + `.btn-sm`) so the
                  stack visibly reads as ONE primary + a couple of secondary
                  utilities, not 4 identical bars. Same handlers/testids --
                  only size/layout changed. */}
              <div className="pause-menu-actions-row">
                <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenSettings}>
                  Settings
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenHelp}>
                  How to Play
                </button>
              </div>
              <button type="button" className="btn btn-link" onClick={onLeave}>
                Leave
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="title-sans">Share this room</h2>
            <ShareRoom code={code} />
            <button type="button" className="btn btn-link" onClick={() => setView('menu')} autoFocus>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
